import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { Helmet } from "react-helmet";
import Card from "../Card";
import Button from "../Button";
import { ReactComponent as ClipIcon } from "../assets/clip.svg";
import amazonLogo from "../assets/amazon-logo.png";


export default function ResultPage() {
    const { main_genre } = useParams();
    const navigate = useNavigate();
    const [showSharePopup, setShowSharePopup] = useState(false);
  
    const handleRetry = () => {
      navigate("/personal");
    };
  
    const [characterData, setCharacterData] = useState(null);
    const [gameDetails, setGameDetails] = useState([]);
    const [valid, setValid] = useState(true);
  
    useEffect(() => {
      const fetchCharacter = async () => {
        const snapshot = await getDocs(collection(db, "characters"));
        const all = snapshot.docs.map((doc) => doc.data());
        const found = all.find((c) => c.main_genre === main_genre);
        if (!found) {
          setValid(false);
        } else {
          setCharacterData(found);
        }
      };
  
      const fetchGames = async () => {
        const snapshot = await getDocs(collection(db, "games"));
        const games = snapshot.docs.map((doc) => doc.data());
        setGameDetails(games);
      };
  
      fetchCharacter();
      fetchGames();
    }, [main_genre]);
  
    if (!valid) return <Navigate to="/" />;
    if (!characterData) return <div className="text-center mt-10">読み込み中...</div>;
  
    const filteredGames = gameDetails.filter((g) => g.main_genre === characterData.main_genre).slice(0, 3);
  
    return (
      <>
        <Helmet>
          <title>{characterData.main_genre_jp}タイプ - ボドゲ診断</title>
          <meta property="og:title" content={`あなたは${characterData.main_genre_jp}タイプ！`} />
          <meta property="og:description" content={characterData.strengths} />
          <meta property="og:image" content={characterData.genre_ogp} />
          <meta property="og:url" content={`https://bodogeshindan.jp/result/${main_genre}`} />
          <meta name="twitter:card" content="summary_large_image" />
        </Helmet>
  
        <div className="flex justify-center items-center p-6 bg-[#efeeea] min-h-screen">
          <Card className="w-full max-w-5xl p-8 text-center shadow-lg rounded-2xl relative">
            <div className="w-full flex flex-col items-center justify-center mb-8 relative">
              <div
                className="text-[250px] font-bold absolute opacity-20"
                style={{
                  color: characterData.color,
                  zIndex: 0,
                  top: "50px"
                }}
              >
                {characterData.main_genre}
              </div>
              <img
                src={characterData.character_img_p}
                alt={characterData.main_genre}
                className="w-[650px] mb-4 relative z-10"
                style={{
                  filter: "drop-shadow(4px 6px 6px rgba(0, 0, 0, 0.3))"
                }}
              />
            </div>
  
            {/* ✅ 修正したボタン表示部 */}
            <div className="flex justify-center gap-6 mb-8">
              <Button
                onClick={handleRetry}
                className="bg-[#2f2301] text-white rounded-full px-4 py-2 shadow-md"
              >
                もう一度診断する
              </Button>
              <Button
                onClick={() => setShowSharePopup(true)}
                style={{ backgroundColor: characterData.color }}
                className="text-white font-semibold py-2 px-6 rounded-full shadow-lg"
              >
                結果を𝕏でシェアする
              </Button>
            </div>
  
            <h2 className="text-lg font-semibold">あなたのボドゲタイプは</h2>
            <p className="text-sm mb-2">{characterData.strengths}</p>
            <p
              className="text-3xl font-bold mb-2 px-4 py-1 inline-block rounded-[5px]"
              style={{ backgroundColor: characterData.color, color: "#fff" }}
            >
              {characterData.main_genre_jp}
            </p>
            <p className="text-sm text-gray-600 mb-6">{characterData.personality}</p>
            <p className="mt-6">あなたにピッタリのゲームを見る</p>
            <div className="text-xl animate-bounce mb-6">↓</div>
  
            <div
              className="w-full py-12 px-6 rounded-t-3xl"
              style={{ backgroundColor: characterData.color }}
            >
              <div className="flex justify-center gap-6 flex-wrap">
                {filteredGames.map((game, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 w-72 shadow-md relative">
                    <div
                      className="absolute top-2 left-2 z-20 text-xs px-2 py-1 rounded-full text-white"
                      style={{ backgroundColor: characterData.color }}
                    >
                      {characterData.main_genre_jp}タイプ
                    </div>
                    <div className="absolute top-2 right-2 z-30">
                      <div className="relative w-32 h-16 transform rotate-[5deg]">
                        <div className="absolute top-0 left-0 w-full h-full bg-white p-1 rounded shadow-md z-0">
                          <a
                            href={game.amazonLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full h-full flex items-center justify-center"
                          >
                            <img src={amazonLogo} alt="Amazon Logo" className="w-full h-full object-contain" />
                          </a>
                        </div>
                        <div className="absolute top-[-20px] right-[-15px] z-10">
                          <ClipIcon className="w-6 h-6 text-yellow-600" />
                        </div>
                      </div>
                    </div>
                    <img src={game.image} alt={game.name} className="w-full h-auto rounded mb-2 mt-6" />
                    <p className="text-sm text-gray-700 mb-1 font-semibold">難易度: {game.difficulty}</p>
                    <p className="text-xs text-gray-600 mb-3">{game.description}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-800">
                      <div>👥 {game.minPlayers}〜{game.maxPlayers}人</div>
                      <div>⏱ {game.time}</div>
                      <div>🎯 {game.age}</div>
                      <div>
                        🏢 <a href={game.publisherLink} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">
                          {game.publisher}
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
  
            {/* 予備のシェアボタン（任意） */}
            <div className="mt-8">
              <Button
                onClick={() => {
                  const text = encodeURIComponent(
                    `【ボドゲ診断結果】\nわたしは${characterData.strengths}な「${characterData.main_genre_jp}」タイプです！\n#ボドゲ診断 #${characterData.main_genre_jp}タイプと繋がりたい`
                  );
                  const url = encodeURIComponent(`https://bodogeshindan.jp/g/${main_genre}.html`);
                  const shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
                  window.open(shareUrl, "_blank");
                }}
                style={{ backgroundColor: characterData.color }}
                className="text-white font-semibold py-2 px-6 rounded-full shadow-lg"
              >
                結果を𝕏でシェアする
              </Button>
            </div>
            {showSharePopup && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
                    onClick={() => setShowSharePopup(false)} // 外枠クリックで閉じる
                >
                    <div
                    className="bg-white bg-opacity-70 rounded-2xl p-8 w-[90%] max-w-md text-center relative"
                    onClick={(e) => e.stopPropagation()} // 中クリックで閉じない
                    >
                    {/* ✕ボタン */}
                    <button
                        className="absolute top-3 right-4 text-[#2f2301] text-xl font-bold"
                        onClick={() => setShowSharePopup(false)}
                    >
                        ✕
                    </button>

                    <p className="text-[#2f2301] text-base font-semibold mb-4">
                        診断結果をシェアしてボドゲ仲間を見つけよう！
                    </p>
                    <img
                        src={characterData.genre_ogp}
                        alt="診断結果のOGP画像"
                        className="w-full mb-4 rounded shadow"
                    />
                    <p className="text-[#2f2301] text-sm mb-4">
                        下のボタンをクリックすると𝕏の投稿下書きが作成されます。
                    </p>
                    <Button
                        onClick={() => {
                        const text = encodeURIComponent(
                            `【ボドゲ診断結果】\nわたしは${characterData.strengths}な「${characterData.main_genre_jp}」タイプです！\n#ボドゲ診断 #${characterData.main_genre_jp}タイプと繋がりたい`
                        );
                        const url = encodeURIComponent(`https://bodogeshindan.jp/g/${main_genre}.html`);
                        const shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
                        window.open(shareUrl, "_blank");
                        setShowSharePopup(false);
                        }}
                        style={{ backgroundColor: characterData.color }}
                        className="text-white font-semibold py-2 px-6 rounded-full shadow-lg"
                    >
                        結果を𝕏でシェアする
                    </Button>
                    </div>
                </div>
                )}
          </Card>
        </div>
      </>
    );
  }
  